import { SuggestedSearchResponse } from "@api-types/suggested-search";
import { SuggestedSearchListItem } from "@data-types/suggested-search";
import { SuggestedSearchListItemType } from "@data-types/suggested-search/SuggestedSearchListItemType";

const COMBINING_MARKS = /[\u0300-\u036f]/g;
const TOKEN_SEPARATORS = /[\s-]+/;
const LOWERCASE_A_CHAR_CODE = 97;
const ALPHABET_LENGTH = 26;

export class SearchUtility {
	static transformSearchList(
		response: SuggestedSearchResponse
	): SuggestedSearchListItem[] {
		const data = response;
		const items = data.items;
		const lookups = data.lookup;

		const results = new Array(items.length);
		const tournaments = new Map();

		for (let i = 0; i < items.length; i++) {
			const item = items[i];

			const category = lookups.categories[item.categoryId] || null;
			const sport = lookups.sports[item.sportId] || null;
			const teamOne = lookups.teams[item.teamOneId] || null;
			const teamTwo = lookups.teams[item.teamTwoId] || null;
			const tournamentId = item.tournamentId;
			const eventId = item.eventId;
			const tournament = lookups.tournaments[tournamentId] || null;

			const teamOneSearch = teamOne?.name
				? this.splitName(teamOne.name)
				: [];
			const teamTwoSearch = teamTwo?.name
				? this.splitName(teamTwo.name)
				: [];
			const tournamentSearch = tournament?.name
				? this.splitName(tournament.name)
				: [];

			const teamsSearch = Array.from(
				new Set([...teamOneSearch, ...teamTwoSearch])
			);

			results[i] = {
				type: SuggestedSearchListItemType.Event,
				eventId,
				category,
				sport,
				teamOne,
				teamTwo,
				tournament: tournament
					? {
							name: tournament.name,
							abrv: tournament.abrv,
							showCountryFlag: tournament.showCountryFlag,
						}
					: null,
				search1: teamsSearch,
				search2: tournamentSearch,
				search1Mask: this.getFirstCharMask(teamsSearch),
				search2Mask: this.getFirstCharMask(tournamentSearch),
				key: `${tournamentId}-${eventId}`,
			};

			if (!tournaments.has(tournamentId)) {
				tournaments.set(tournamentId, {
					type: SuggestedSearchListItemType.Tournament,
					tournamentId,
					details: tournament,
					category,
					sport,
					events: new Set([eventId]),
					search1: tournamentSearch,
					search2: new Set([...teamsSearch]),
					search1Mask: this.getFirstCharMask(tournamentSearch),
					key: `${tournamentId}`,
				});
			} else {
				const existingTournament = tournaments.get(tournamentId);
				existingTournament.events.add(eventId);
				teamsSearch.forEach((name) =>
					existingTournament.search2.add(name)
				);
			}
		}

		const tournamentResults = Array.from(tournaments.values()).map((t) => {
			const search2 = Array.from(t.search2) as string[];

			return {
				...t,
				search2,
				search2Mask: this.getFirstCharMask(search2),
				events: Array.from(t.events),
			};
		});

		const mergedResults = results.concat(tournamentResults);

		return mergedResults;
	}

	static normalizeText(value: string) {
		return value
			.toLowerCase()
			.normalize("NFD")
			.replace(COMBINING_MARKS, "");
	}

	static splitName(name: string, minWordLength = 1) {
		const normalizedName = this.normalizeText(name);
		let words = [];
		let currentWord = "";

		for (let i = 0, len = normalizedName.length; i < len; i++) {
			const char = normalizedName[i];

			if (char === "-" || char === " ") {
				if (currentWord.length >= minWordLength)
					words.push(currentWord);
				currentWord = "";
			} else {
				currentWord += char;
			}
		}

		if (currentWord.length >= minWordLength) {
			words.push(currentWord);
		}
		return words;
	}

	private static getFirstCharBit(word: string) {
		const offset = word.charCodeAt(0) - LOWERCASE_A_CHAR_CODE;
		return offset >= 0 && offset < ALPHABET_LENGTH
			? offset
			: ALPHABET_LENGTH;
	}

	static getFirstCharMask(words: string[]) {
		let mask = 0;
		for (let i = 0, len = words.length; i < len; i++) {
			if (words[i]) {
				mask |= 1 << this.getFirstCharBit(words[i]);
			}
		}
		return mask;
	}

	private static hasTokenWithPrefix(words: string[], prefix: string) {
		for (let i = 0, len = words.length; i < len; i++) {
			if (words[i].startsWith(prefix)) {
				return true;
			}
		}
		return false;
	}

	static searchItems(
		items: SuggestedSearchListItem[],
		query: string,
		minQueryLength: number
	) {
		if (!query || query.length < minQueryLength) return [];

		const queryTokens = this.normalizeText(query)
			.split(TOKEN_SEPARATORS)
			.filter(Boolean)
			.sort((a, b) => b.length - a.length);

		if (queryTokens.length === 0) return [];

		const tokenBits = queryTokens.map(
			(token) => 1 << this.getFirstCharBit(token)
		);

		const results = [];

		for (const item of items) {
			let matchedInSearch1 = true;
			let matchedAll = true;

			for (let t = 0, count = queryTokens.length; t < count; t++) {
				const token = queryTokens[t];
				const bit = tokenBits[t];

				if (
					(item.search1Mask & bit) !== 0 &&
					this.hasTokenWithPrefix(item.search1, token)
				) {
					continue;
				}

				matchedInSearch1 = false;

				if (
					(item.search2Mask & bit) === 0 ||
					!this.hasTokenWithPrefix(item.search2, token)
				) {
					matchedAll = false;
					break;
				}
			}

			if (!matchedAll) {
				continue;
			}

			const isEvent = item.type === SuggestedSearchListItemType.Event;
			const priority = matchedInSearch1
				? isEvent
					? 1
					: 2
				: isEvent
					? 3
					: 4;

			results.push({ item, priority });
		}

		const searchResult = results
			.sort((a, b) => a.priority - b.priority)
			.map((entry) => entry.item);

		return searchResult;
	}
}
